import { calculateOpacityHex, HexToRGBA } from "./OpacityUtils.js";
import { getSlideImageBackgroundSize } from "./slideImageFit.js";

// Saved on the image style as ElementImageShape. A missing or unknown value is Original,
// so images saved before shapes existed draw exactly as before.
export const IMAGE_SHAPE = {
  ORIGINAL: "original",
  SQUARE: "square",
  ROUNDED: "rounded",
  CIRCLE: "circle",
  ARCH: "arch",
};
const IMAGE_SHAPES = Object.values(IMAGE_SHAPE);
export const DEFAULT_IMAGE_SHAPE = IMAGE_SHAPE.ORIGINAL;
const ROUNDED_IMAGE_RADIUS = 24;

export const resolveImageShape = (shape) => (IMAGE_SHAPES.includes(shape) ? shape : DEFAULT_IMAGE_SHAPE);

// Square and Circle have a 1:1 box; the image is drawn into it with its own fit mode.
export const isSquareImageShape = (shape) => {
  const resolved = resolveImageShape(shape);
  return resolved === IMAGE_SHAPE.SQUARE || resolved === IMAGE_SHAPE.CIRCLE;
};

// ImageAspect is only stored once the picture has loaded; old data may not have it.
export const isValidImageAspect = (aspect) => typeof aspect === "number" && Number.isFinite(aspect) && aspect > 0;

// The aspect the canvas locks a resize to (react-rnd lockAspectRatio): 1:1 for Square/Circle, else the
// picture's own aspect once known. false leaves the resize free (non-images, or before the picture loads).
export const getImageResizeAspect = (style) => {
  if (isSquareImageShape(style?.ElementImageShape)) return 1;
  return isValidImageAspect(style?.ImageAspect) ? style.ImageAspect : false;
};

// Also used by the outer frames that paint a background colour, so it doesn't show past a Circle or Arch.
export const getImageShapeBorderRadius = (style) => {
  const s = style || {};
  switch (resolveImageShape(s.ElementImageShape)) {
    case IMAGE_SHAPE.SQUARE:
      return "0px";
    case IMAGE_SHAPE.ROUNDED:
      return ROUNDED_IMAGE_RADIUS + "px";
    case IMAGE_SHAPE.CIRCLE:
      return "50%";
    case IMAGE_SHAPE.ARCH:
      // Oversized radii get scaled down by the browser to half the width: a full round top on any box.
      return "9999px 9999px 0 0";
    default:
      return s.ElementBorderRadius + "px";
  }
};

// The fade layer is drawn in the page colour. Older callers may not pass a page; the element colour keeps their old look.
const getImageFadeColor = (style, page) =>
  isHexColor(page?.PageBackgroundColor) ? page.PageBackgroundColor : style.ElementBackgroundColor;

// HexToRGBA draws no layer for opacity 0, so only 0 < opacity < 1 is a fade.
const hasImageFade = (style) => typeof style.ElementOpacity === "number" && style.ElementOpacity > 0 && style.ElementOpacity < 1;

// The colour behind the picture (letterbox gaps of Fit inside, outer frames). A faded image uses the page
// colour like its fade layer, so the gaps don't show the element colour; without a fade it is unchanged.
export const getImageFrameBackgroundColor = (style, page) => {
  const s = style || {};
  const color = hasImageFade(s) ? getImageFadeColor(s, page) : s.ElementBackgroundColor;
  return color + calculateOpacityHex(s.ElementOpacity);
};

/**
 * CSS for the inner box of an Image element: corner rounding per shape (which also clips the background
 * layers) plus the stacked background (ElementOpacity layer over the image), fit and tiling.
 * Shared by the Slides/Newsletter editors, print and template renderers so they can't drift apart.
 * `page` is the element's page: the fade layer is drawn in its background colour, so an image fades
 * towards the page like a background image does.
 */
export function buildImageElementStyle(style, page) {
  const s = style || {};
  const tiled = s.ElementBackgroundImageRepeatStatus === true;
  const layer = () => `rgba(${HexToRGBA(getImageFadeColor(s, page), s.ElementOpacity)})`;

  return {
    backgroundImage:
      s.ElementBackgroundImage !== null
        ? `linear-gradient(${layer()},${layer()}),  url(${s.ElementBackgroundImage})`
        : "",
    backgroundColor: getImageFrameBackgroundColor(s, page),
    backgroundRepeat: tiled ? "repeat" : "no-repeat",
    backgroundPosition: tiled ? "left top" : "center",
    backgroundSize: getSlideImageBackgroundSize(s.ElementBackgroundImageRepeatStatus, s.ElementImageFitMode),
    borderRadius: getImageShapeBorderRadius(s),
  };
}

const isHexColor = (value) => typeof value === "string" && /^#?[0-9a-fA-F]{6}$/.test(value);
